import { useIntl } from 'react-intl';

import { Colors } from 'app/shared/theme';
import { calcEventState } from 'app/shared/utils/calcEventState';
import {
  AttendeeState,
  Event,
  EventForCompactEventCard,
} from 'app/shared/typings';

interface AttendeeStatusProps {
  statusMessage?: string;
  messageType?: keyof Colors['messaging'];
  grayscale: boolean;
}

interface EventAndAttendeeMessagingProps {
  event?: Event | EventForCompactEventCard;
  attendeeState?: AttendeeState;
  numTickets?: number;
}

export const useEventAndAttendeeStatusMessaging = ({
  event,
  attendeeState,
  numTickets = 0,
}: EventAndAttendeeMessagingProps): AttendeeStatusProps => {
  const { formatMessage } = useIntl();

  const status = calcEventState({
    event,
    attendeeState,
  });
  const isCompetitionEvent = !!event?.competition?.id;
  const isBuyFlowEvent = event?.attendeeFlow === 'buy';

  const messages = {
    applied: formatMessage({ id: 'event.statusMessage.applied' }),
    cancelled: formatMessage({ id: 'event.statusMessage.canceled' }),
    cantGo: formatMessage({ id: 'event.statusMessage.cantGo' }),
    confirmed: formatMessage({ id: 'event.statusMessage.confirmed' }),
    entered: formatMessage({ id: 'event.statusMessage.entered' }),
    expired: formatMessage({ id: 'event.statusMessage.expired' }),
    guestlisted: formatMessage({ id: 'event.statusMessage.guestlisted' }),
    paused: formatMessage({ id: 'event.statusMessage.paused' }),
    reserved: formatMessage(
      { id: 'event.statusMessage.reserved' },
      { numTickets }
    ),
    soldOut: formatMessage({ id: 'event.statusMessage.soldOut' }),
    unavailable: formatMessage({ id: 'event.statusMessage.unavailable' }),
    winner: formatMessage({ id: 'event.statusMessage.winner' }),
  };

  switch (status) {
    case 'applied':
      return {
        statusMessage: isCompetitionEvent ? messages.entered : messages.applied,
        messageType: 'info',
        grayscale: false,
      };
    case 'cancelled':
      return {
        statusMessage: messages.cancelled,
        messageType: 'error',
        grayscale: true,
      };
    case 'cant_go':
      return {
        statusMessage: messages.cantGo,
        messageType: 'info',
        grayscale: false,
      };
    case 'confirmed':
      return {
        statusMessage: messages.confirmed,
        messageType: 'success',
        grayscale: false,
      };
    case 'expired':
      return {
        statusMessage: messages.expired,
        messageType: 'error',
        grayscale: false,
      };
    case 'guestlisted':
      return {
        statusMessage: isCompetitionEvent
          ? messages.winner
          : messages.guestlisted,
        messageType: 'success',
        grayscale: false,
      };
    case 'paused':
      return {
        statusMessage:
          isBuyFlowEvent && !isCompetitionEvent
            ? messages.paused
            : messages.unavailable,
        messageType: 'warning',
        grayscale: true,
      };
    case 'reserved':
      return {
        statusMessage: messages.reserved,
        messageType: 'success',
        grayscale: false,
      };
    case 'soldout':
      return {
        statusMessage: messages.soldOut,
        messageType: 'error',
        grayscale: true,
      };
    default:
      return {
        grayscale: false,
      };
  }
};
